import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ScrollToTopButtonComponent } from '../templates/scroll-to-top-button/scroll-to-top-button.component';
import { FormsModule } from '@angular/forms';
import { FaminfoService } from '../services/faminfo.service';
import { QuillModule } from 'ngx-quill';
import { ScrollService } from '../services/scroll.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommentService } from '../services/comment.service';
import { CapitalizePipe } from "../pipes/capitalize.pipe";
import { AllpagesService } from '../services/allpages.service';
import { LoadingService } from '../services/loading.service';

@Component({
    selector: 'app-fam-infos',
    standalone: true,
    imports: [CommonModule, RouterModule, ScrollToTopButtonComponent, FormsModule, QuillModule, CapitalizePipe],
    templateUrl: './fam-infos.component.html',
    styleUrl: './fam-infos.component.scss'
})
export class FamInfosComponent implements OnInit {
  //public imageCache: { [id: string]: { original: string; thumbnail: string }[] } = {};
  public imageFiles: File[] = [];
  public pdfCache: { [id: string]: { url: string; name: string }[] } = {};
  public pdfFiles: { file: File; name: string; customName?: string }[] = [];
  deletedImages: Set<string> = new Set();
  deletedPdfs: Set<string> = new Set();
  userId: string | null = null;
  userEmail: string | null = null;
  infos: any[] = [];
  selectedInfo: any = null;
  info = {
    title: '',
    content: '',
    image_1: null,
    image_2: null,
    image_3: null,
    image_4: null,
    pdf_1: null,
    pdf_2: null,
    pdf_3: null,
    pdf_4: null,
  };
  filteredInfos: any[] = [];
  entry: any = null;
  entryToDelete: any = null;
  sanitizedContent: SafeHtml | null = null;
  showImageUrl: string | null = null;
  clearedFields: string[] = [];
  comments: { [key: number]: any[] } = {};
  comment: any = null;
  newComment: { [key: number]: string } = {};
  commentToUpdate: any;
  commentToDelete: any = null;
  thumbnailPath = 'infos/thumbnails/';
  family_1: string | null;
  family_2: string | null;
  families: string[] = [];


  constructor(
    public faminfoService: FaminfoService,
    private commentService: CommentService,
    private scrollService: ScrollService,
    public sanitizer: DomSanitizer,
    public allpagesService: AllpagesService,
    private loadingService: LoadingService,
  ) {
    this.family_1 = localStorage.getItem('family_1');
    this.family_2 = localStorage.getItem('family_2');
  }

  /**
   * Initializes the component by loading all information and retrieving user data from session storage.
   */
  ngOnInit(): void {
    this.loadAllInfo();
    this.userId = localStorage.getItem('userId');
    this.userEmail = localStorage.getItem('userEmail');
  }

  loadAllInfo(): void {
    this.loadingService.show(); // Lade-Overlay aktivieren
    let images;
    let pdfs;
    this.faminfoService.getAllInfos().subscribe({
      next: (infos) => {
        this.infos = infos.map((info: any) => {
          images = this.allpagesService.prepareImages(info)
          pdfs = this.allpagesService.preparePdfs(info);
          return {
            ...info,
            isHidden: false,
            images,
            pdfs
          };
        });

        this.infos.sort((a, b) => {
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        });

        this.filteredInfos = this.infos;
        this.loadComments();
      },
      error: (error) => {
        console.error('Fehler beim Laden:', error);
        this.loadingService.hide();
        if (error.status === 404) {
          alert('Keine Infos gefunden.');
        } else if (error.status === 500) {
          alert('Serverfehler. Bitte versuche es erneut.');
        } else {
          alert('Fehler beim Laden.');
        }
      },
      complete: () => {
        this.loadingService.hide(); // Lade-Overlay ausblenden
      }
    });
  }


  /**
   * Filters the list of information items based on the search term from the event input.
   *
   * @param {Event} event - The input event containing the search term.
   */
  filterEntries(event: Event): void {
    const target = event.target as HTMLInputElement;
    const searchTerm = target.value.toLowerCase();

    if (!searchTerm) {
      this.filteredInfos = this.infos;
    } else {
      this.filteredInfos = this.infos.filter((info: { author_name: string; title: string; content: string; family_1: string; family_2: string }) =>
        info.author_name.toLowerCase().includes(searchTerm) ||
        (info.title && info.title.toLowerCase().includes(searchTerm)) ||
        info.content.toLowerCase().includes(searchTerm) ||
        (info.family_1 && info.family_1.toLowerCase().includes(searchTerm)) ||
        (info.family_2 && info.family_2.toLowerCase().includes(searchTerm))
      );
    }
  }

  /**
   * Scrolls the page to the element with the specified info ID.
   *
   * @param {number} infoId - The ID of the info item to scroll to.
   */
  goToInfo(infoId: number): void {
    const element = document.getElementById(infoId.toString());
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  /**
   * toggles visibility of each entry
   *
   * @param {number} index - number of the *ngFor.
   */
  toggleVisibility(index: number): void {
    this.infos[index].isHidden = !this.infos[index].isHidden;
  }


  /**
   * Handles file input changes and updates the image files array.
   *
   * @param {any} event - The change event from the file input.
   * @param {number} index - The index of the image slot being updated.
   */
  onImageFileChange(event: any, index: number) {
    const files = event.target.files;

    if (files.length > 0) {
      const file = files[0];

      if (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg') {

        if (this.imageFiles[index - 1]) {
          this.imageFiles.splice(index - 1, 1, file);
        } else {
          this.imageFiles.push(file);
        }

      } else {
        alert('Nur JPG, PNG und JPEG Dateien sind erlaubt.');
      }
    }
  }


  onPdfFileChange(event: any, index: number) {
    const files = event.target.files;

    if (files.length > 0) {
      const file = files[0];

      if (file.type === 'application/pdf') {
        const pdfObj = {
          file,
          name: file.name,     // Original-Dateiname
          customName: ''       // leer, Nutzer vergibt eigenen Namen
        };

        if (this.pdfFiles[index - 1]) {
          this.pdfFiles.splice(index - 1, 1, pdfObj);
        } else {
          this.pdfFiles.push(pdfObj);
        }

      } else {
        alert('Nur Pdf Dateien sind erlaubt.');
      }
    }
  }

  removeNewPdf(index: number): void {
    this.pdfFiles.splice(index, 1);
  }

  /**
   * Displays a pop-up based on the specified mode and data.
   * The mode determines what type of pop-up to show (e.g., add, edit, delete).
   *
   * @param {string} mode - The mode for the pop-up (e.g., 'add', 'editInfo', 'image', 'deleteInfo', 'editComment', 'deleteComment').
   * @param {any} data - The data to be used in the pop-up, which varies depending on the mode.
   */
  showPopUp(mode: string, data: any) {
    if (mode === 'add') {
      this.entry = {
        title: '',
        content: '',
        image_1: null,
        image_2: null,
        image_3: null,
        image_4: null,
        family_1: '',
        family_2: '',
        pdf_1: null,
        pdf_2: null,
        pdf_3: null,
        pdf_4: null,
      };
    } else if (mode === 'edit') {
      this.entry = { ...data };
    } else if (mode === 'image') {
      this.entry = null;
      this.showImageUrl = data;
    } else if (mode === 'delete') {
      this.entry = null;
      this.entryToDelete = { ...data };
    } else if (mode === 'editComment') {
      this.commentToUpdate = { ...data };
    } else if (mode === 'deleteComment') {
      this.commentToDelete = { ...data };
    }

    const popUpContainer = document.getElementById('popUpContainer');
    if (popUpContainer) {
      popUpContainer.classList.remove('dNone');
    }

    const popUp = document.getElementById('popUp');
    if (popUp) {
      this.scrollService.setActiveScrollContainer(popUp);
    }
  }


  /**
   * Removes an image by its URL from the entry and marks it as deleted.
   *
   * @param {string} imageUrl - The URL of the image to be removed.
   */
  removeImageByUrl(imageUrl: string): void {
    if (this.entry.image_1_url === imageUrl) {
      this.entry.image_1_url = null;
      this.entry.image_1 = null;
    } else if (this.entry.image_2_url === imageUrl) {
      this.entry.image_2_url = null;
      this.entry.image_2 = null;
    } else if (this.entry.image_3_url === imageUrl) {
      this.entry.image_3_url = null;
      this.entry.image_3 = null;
    } else if (this.entry.image_4_url === imageUrl) {
      this.entry.image_4_url = null;
      this.entry.image_4 = null;
    }
    this.deletedImages.add(imageUrl);
  }


  removePdfByUrl(pdfUrl: string): void {
    if (this.entry.pdf_1_url === pdfUrl) {
      this.entry.pdf_1_url = null;
      this.entry.pdf_1 = null;
    } else if (this.entry.pdf_2_url === pdfUrl) {
      this.entry.pdf_2_url = null;
      this.entry.pdf_2 = null;
    } else if (this.entry.pdf_3_url === pdfUrl) {
      this.entry.pdf_3_url = null;
      this.entry.pdf_3 = null;
    } else if (this.entry.pdf_4_url === pdfUrl) {
      this.entry.pdf_4_url = null;
      this.entry.pdf_4 = null;
    }
    this.deletedPdfs.add(pdfUrl);
  }

  isImageDeleted(imageUrl: string, entry: any): boolean {
    return this.deletedImages.has(imageUrl); // Überprüfe, ob das Bild gelöscht ist
  }

  isPdfDeleted(url: string): boolean {
    return this.deletedPdfs.has(url);
  }

  triggerPdfUpload(index: number): void {
    document.getElementById('pdf_' + index)?.click();
  }

  /**
   * Checks if an image slot is available for a new image.
   *
   * @param {number} index - The index of the image slot to check (1 to 4).
   * @returns {boolean} `true` if the image slot is available, otherwise `false`.
   */
  isImageSlotAvailable(index: number): boolean {
    return !this.entry[`image_${index}`];
  }

  isPdfSlotAvailable(index: number): boolean {
    return !this.entry[`pdf_${index}`];
  }

  /**
   * Hides the pop-up and resets the state of various properties related to entries and comments.
   */
  hidePopUp() {
    this.entry = null;
    this.entryToDelete = null;
    this.showImageUrl = null;
    this.comment = null;
    this.commentToDelete = null;
    this.commentToUpdate = null;
    const mainContainer = document.getElementById('mainContainer');
    const popUpContainer = document.getElementById('popUpContainer');
    this.scrollService.setActiveScrollContainer(mainContainer);
    if (popUpContainer) {
      popUpContainer.classList.add('dNone');
    }
    this.resetEntryForm();
  }

  /**
   * Assembles the form data for the entry, including title, content, family details, and images.
   * differs from recipe due to family information
   * @returns {FormData} The assembled form data.
   */
  assembleFormData(): FormData {
    const formData = new FormData();
    formData.append('title', this.entry.title);
    formData.append('content', this.entry.content);
    if (this.family_1 == 'null' && this.family_2 != null) {
      this.entry.family_1 = this.family_2;
    } else if (this.family_2 == 'null' && this.family_1 != null) {
      this.entry.family_1 = this.family_1;
    }
    if (this.entry.family_1) {
      formData.append('family_1', this.entry.family_1);
    }
    if (this.entry.family_2) {
      formData.append('family_2', this.entry.family_2);
    }
    this.allpagesService.addNewImages(this.entry, this.imageFiles, formData);
    this.allpagesService.addNewPdfs(this.entry, this.pdfFiles, formData);

    this.allpagesService.addNullFields(this.entry, formData);
    this.allpagesService.addNullPdfFields(this.entry, formData);

    return formData;
  }


  /**
   * Saves the entry by either adding a new one or updating an existing one.
   * Displays an alert if no family is selected.
   * differs from recipe due to family info
   */
  saveEntry(): void {
    const formData = this.assembleFormData();
    if (!this.entry.family_1 && !this.entry.family_2) {
      alert('Bitte wähle mindestens eine Familie aus.');
      return;

    }

    if (this.entry.id) {
      this.updateEntry(formData);
    } else {
      this.addEntry(formData);
    }
  }

  /**
   * Updates an existing entry with the given form data.
   * differs from recipe due to service
   * @param {FormData} formData - The form data containing updated information for the entry.
   */
  updateEntry(formData: FormData): void {
    this.loadingService.show();
    this.faminfoService.updateInfo(this.entry.id, formData).subscribe({
      next: (response: any) => {
        this.loadAllInfo();
       // window.location.reload();
      },
      error: (error) => {
        console.error('Fehler beim Aktualisieren:', error);
        this.loadingService.hide();
        if (error.status === 400) {
          const message =
            error.error?.pdf_1?.[0] ??
            'Fehlerhafte Eingabe. Bitte überprüfe deine Daten.';
          alert(message);
        } else if (error.status === 403) {
          alert('Du hast keine Berechtigung für diese Aktion.');
        } else if (error.status === 500) {
          alert('Serverfehler. Bitte versuche es später erneut.');
        } else {
          alert('Unbekannter Fehler. Bitte kontaktiere den Support.');
        }
      },
      complete: () => {
        this.loadingService.hide();
        this.entry = null;
        this.hidePopUp();
      }
    });
  }


  /**
   * Adds a new entry using the provided form data.
   * differs from recipe due to param
   * @param {FormData} formData - The form data containing information for the new entry.
   */
  addEntry(formData: FormData) {
    this.loadingService.show();  // Lade-Overlay anzeigen
    this.faminfoService.addInfo(formData).subscribe({
      next: (response: any) => {
        this.loadAllInfo();
        this.entry.title = '';
        this.entry.content = '';
        // window.location.reload();
      },
      error: (error) => {
        console.error('Fehler beim Aktualisieren:', error);
        this.loadingService.hide();
        // Benutzerfreundliche Fehlermeldung setzen
        if (error.status === 400) {
          const message =
            error.error?.pdf_1?.[0] ??
            'Fehlerhafte Eingabe. Bitte überprüfe deine Daten.';
          alert(message);
        } else if (error.status === 403) {
          alert('Du hast keine Berechtigung für diese Aktion.');
        } else if (error.status === 500) {
          alert('Serverfehler. Bitte versuche es später erneut.');
        } else {
          alert('Unbekannter Fehler. Bitte kontaktiere den Support.');
        }
      },
      complete: () => {
        this.loadingService.hide();  // Lade-Overlay ausblenden
        this.entry = null;
        this.hidePopUp();
      }
    });;
  }

  /**
   * Resets the entry form to its initial state.
   */
  resetEntryForm(): void {
    this.entry = {
      title: '',
      content: '',
      image_1: null,
      image_2: null,
      image_3: null,
      image_4: null,
      pdf_1: null,
      pdf_2: null,
      pdf_3: null,
      pdf_4: null,
    };
    this.imageFiles = [];
    this.pdfFiles = [];
  }

  /**
  * Deletes an entry based on the entry to delete.
  * differs from recipe due to service
  */
  deleteEntry(): void {
    this.loadingService.show();
    this.loadingService.hide();
    this.faminfoService.deleteInfo(this.entryToDelete.id).subscribe({
      next: () => {
        this.infos = this.infos.filter((e: any) => e.id !== this.entryToDelete.id);
        this.loadAllInfo();
        this.hidePopUp();
      },
      error: (error) => {
        console.error('Fehler beim Löschen des Eintrags:', error);

        if (error.status === 404) {
          alert('Eintrag nicht gefunden.');
        } else if (error.status === 500) {
          alert('Serverfehler. Bitte versuche es später erneut.');
        } else {
          alert('Fehler beim Löschen des Eintrags.');
        }
      },
      complete: () => {
        this.loadingService.hide();
      }
    });
  }

  /**
   * Loads comments for each info entry and stores them in the `comments` object.
   * differs from recipe with regard to function in commentservice
   */
  loadComments() {
    this.infos.forEach(info => {
      this.commentService.getCommentsForFamInfo(info.id).subscribe(comments => {
        this.comments[info.id] = comments;
      });
    });
  }

  /**
   * Adds a new comment to the specified info entry.
   * differs from recipe due to key
   * @param {number} infoId - The ID of the info entry to which the comment will be added.
   * @param {string} commentContent - The content of the comment.
   */
  addComment(infoId: number, commentContent: string) {
    const comment = {
      content: commentContent,
      famInfo: infoId
    };
    this.commentService.addComment(comment).subscribe(newComment => {
      if (this.comments[infoId]) {
        this.comments[infoId].push(newComment);
      } else {
        this.comments[infoId] = [newComment];
      }
      this.newComment[infoId] = '';
    });
  }

  /**
   * Deletes a comment based on the comment to delete.
   */
  deleteComment() {
    this.commentService.deleteComment(this.commentToDelete.id).subscribe(() => {
      this.loadAllInfo();
      this.hidePopUp();
    });
  }

  /**
   * Saves updates to a comment.
   */
  saveComment() {
    this.commentService.updateComment(this.commentToUpdate.id, { content: this.commentToUpdate.content }).subscribe((response: any) => {
      const infoId = this.findInfoIdByCommentId(this.commentToUpdate.id);
      const index = this.comments[infoId].findIndex(c => c.id === this.commentToUpdate.id);
      if (index !== -1) {
        this.comments[infoId][index] = response;
        this.commentToUpdate = null;
        this.hidePopUp();
      }
    })
  }

  /**
   * Finds the ID of the info entry associated with a specific comment.
   *
   * @param {number} commentId - The ID of the comment.
   * @returns {number} The ID of the associated info entry, or `0` if not found.
   */
  findInfoIdByCommentId(commentId: number): number {
    for (const infoId in this.comments) {
      if (this.comments[infoId].some(c => c.id === commentId)) {
        return +infoId;
      }
    }
    return 0;
  }


  /**
   * Handles changes in family selection and updates the entry's family fields accordingly.
   *
   * @param {Event} event - The event triggered by the family selection change.
   * @param {'family_1' | 'family_2'} familyKey - The key for the family field being updated.
   */
  onFamilySelectionChange(event: Event, familyKey: 'family_1' | 'family_2') {
    const checkbox = event.target as HTMLInputElement;
    const familyValue = familyKey === 'family_1' ? this.family_1 : this.family_2;

    if (checkbox.checked) {
      if (this.entry.family_1 === '' || this.entry.family_1 === familyValue) {
        this.entry.family_1 = familyValue;
      } else {
        this.entry.family_2 = familyValue;
      }
    } else {
      if (this.entry.family_1 === familyValue) {
        this.entry.family_1 = '';
      } else if (this.entry.family_2 === familyValue) {
        this.entry.family_2 = '';
      }
    }
  }

}
